// Hideo - PED › Timeline (logic without UI; the page is src/pages/TimelinePage.vue).
// Each video is a bar from the START to the END of work.
// - Work start: video.workStart = "YYYY-MM-DDTHH:MM" | '' (local time).
// - End of work = PUBLISH DATE AND TIME (video.publishDate / publishTime): one value for Board, Calendar, Timeline and
//   deadlines (user decision: "the publish date becomes the end of work"). Without a time it is the end of that day
//   (tlEnd, src/features/agenda.ts). The old separate end of work (workEnd) is merged by normalizeData.
// - Like Board and Calendar it uses the plan tabs and the Type and format filters (plan store `visible`), without the
//   published videos; rows are grouped by status in the order of the Board columns (STATUS_KEYS, then "Other statuses").
// - Week view (30-minute steps) and Month view (one-day steps, the time is kept), chosen on the device in
//   "creatorOS_tlview" ('month', anything else = week).
// - A video with an end but no start has a dashed one-day bar (TL_GHOST): dragging its left edge chooses the start,
//   moving it changes only the publication.
// - Marks: green dot = actual delivery (video.doneAt: written when the video becomes "Pronto/Pubblicato", cleared if it
//   moves back, tlMarkDone), striped bar = overdue (end of work passed and video not ready).
// tlMarkDone is also called by the Board (BoardPage.vue).
import { fmtDate, t, tn } from '../i18n'
import type { ProfileData, Series, Video } from '../data/types'
import { tlDur, tlEnd, tlParse, tlStart } from './agenda'
import { seriesOf } from './formats'
import { isLate, videoTitle } from './ped'
import { safeColor } from '../utils/color'
import { addDays, addMonths, firstOfMonth, isoDay, mondayOf, spanTitle, weekOfMonth } from '../utils/date'
import { STATUS_KEYS, STATUS_READY } from '../data/contentTypes'

export const TL_VIEW_KEY = 'creatorOS_tlview'
/** Length of the dashed bar of a video without a start (one day before the end of work) */
export const TL_GHOST = 86400000
export type TlView = 'week' | 'month'
export type TlMode = 'move' | 'start' | 'end'
const HALF_HOUR = 1800000

/** Stored view: 'month', anything else is the week */
export const parseTlView = (raw: string | null): TlView => (raw === 'month' ? 'month' : 'week')

// ===== Dates =====
/** "HH:MM" of a date */
export const tlHhmm = (d: Date): string => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
/** Date → "YYYY-MM-DDTHH:MM" (local time), the format of workStart, doneAt and publishedAt */
export const tlFmt = (d: Date): string => `${isoDay(d)}T${tlHhmm(d)}`
/** "Wed 14 Oct 09:30" */
export const tlNice = (d: Date): string => fmtDate(d, { weekday: 'short', day: 'numeric', month: 'short' }) + ' ' + tlHhmm(d)
/** End of work as written by the user: "Wed 14 Oct" plus the time when there is one ('' without a date) */
const tlEndText = (v: Pick<Video, 'publishDate' | 'publishTime'>): string =>
  v.publishDate ? fmtDate(new Date(v.publishDate + 'T12:00'), { weekday: 'short', day: 'numeric', month: 'short' }) + (v.publishTime ? ' ' + v.publishTime : '') : ''

/** Actual delivery: when the video became "Pronto/Pubblicato" (the first time is kept); cleared when it moves back */
export function tlMarkDone(v: Video, before: Video | null | undefined, now = new Date()): void {
  const kept = before && before.status === STATUS_READY ? before.doneAt : ''
  v.doneAt = v.status === STATUS_READY ? kept || tlFmt(now) : ''
}
/** Writes the end of work (= publication). keepNoTime: a video without a time stays without one (Month view) */
export function tlSetEnd(v: Video, d: Date, keepNoTime: boolean): void {
  v.publishDate = isoDay(d)
  if (!(keepNoTime && !v.publishTime)) v.publishTime = tlHhmm(d)
}

// ===== Period shown =====
interface TlRange {
  /** First day shown, 00:00 */
  t0: Date
  /** The day after the last one, 00:00 */
  t1: Date
  days: number
  /** t1 - t0 in milliseconds */
  span: number
}
/** First day of the period that contains a date: the 1st of its month, or its Monday */
export const tlFirstDay = (view: TlView, d: Date): Date => (view === 'month' ? firstOfMonth(d) : mondayOf(d))
export function tlRange(view: TlView, from: Date): TlRange {
  const t0 = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const days = view === 'month' ? new Date(t0.getFullYear(), t0.getMonth() + 1, 0).getDate() : 7
  const t1 = new Date(t0.getFullYear(), t0.getMonth(), t0.getDate() + days)
  return { t0, t1, days, span: +t1 - +t0 }
}
/** First day after changing view: the month shown → its month; a week → the week of today if it is in the current
 *  month, otherwise the first week of that month */
export const tlViewFrom = (view: TlView, from: Date, now: Date): Date => (view === 'month' ? firstOfMonth(from) : weekOfMonth(from, now))
/** Previous (-1) or next (1) period */
export const tlNavFrom = (view: TlView, from: Date, n: number): Date => (view === 'month' ? addMonths(from, n) : addDays(from, n * 7))
/** Title of the period: "October 2026", "12–18 Oct 2026" or "28 Sept – 4 Oct 2026" */
export function tlTitle(view: TlView, R: TlRange): string {
  const last = new Date(+R.t1 - 86400000)
  if (view === 'month') return fmtDate(R.t0, { month: 'long', year: 'numeric' })
  return spanTitle(R.t0, last)
}
/** Position of a moment in the period, 0–100 (%) */
const tlPct = (R: TlRange, at: Date | number): number => Math.max(0, Math.min(100, (+at - +R.t0) / R.span * 100))

// ===== Bars =====
interface TlSpan {
  s: Date
  e: Date
  /** No start: dashed bar from one day before the end */
  ghost: boolean
}
/** Bar of a video: from the start (or one day before the end, dashed) to the end of work; null without an end */
export function tlSpan(v: Video): TlSpan | null {
  const e = tlEnd(v)
  if (!e) return null
  const s = tlStart(v)
  return { s: s || new Date(+e - TL_GHOST), e, ghost: !s }
}
/** Left and width of a bar (%): at least 0.8% wide */
export function tlBarBox(R: TlRange, s: Date | number, e: Date | number): { left: number; width: number } {
  const left = tlPct(R, s)
  return { left, width: Math.max(tlPct(R, e) - left, 0.8) }
}
/** Colours of a bar: a format colour (text in onHex) or the main colour; dashed when there is no start */
export function tlBarLook(color: string, ghost: boolean, onHex: (hex: string) => string): { style: string; cls: string } {
  if (ghost) return color ? { style: `background:${color}33;border:1.5px dashed ${color};`, cls: 'text-gray-100' } : { style: '', cls: 'bg-brand/20 border border-dashed border-brand text-gray-100' }
  return color ? { style: `background:${color};color:${onHex(color)};`, cls: '' } : { style: '', cls: 'bg-brand text-white' }
}

interface TlDay {
  iso: string
  today: boolean
  weekend: boolean
  /** Weekday: one letter (month) or short name without the dot (week) */
  label: string
  num: number
}
/** Subtitle of a row: overdue (bold, brand), delivered late (brand), delivered on time (green), or plain */
interface TlRowSub { text: string; kind: 'late' | 'deliveredLate' | 'onTime' | '' }
export interface TlRow {
  v: Video
  title: string
  format: Series | null
  /** Safe format colour, '' without a format */
  color: string
  s: Date
  e: Date
  ghost: boolean
  left: number
  width: number
  late: boolean
  ready: boolean
  /** Starts before the period (arrow at the start of the label) */
  cut: boolean
  /** Position of the delivery dot (%), -1 when not in the period */
  donePct: number
  tip: string
  sub: TlRowSub
}
interface TlGroup {
  status: string
  /** Header: the Board column name (with the Type filter, that Type's words), or "Other statuses" */
  name: string
  rows: TlRow[]
}
interface TlSubPart { text: string; bold: boolean }
interface TimelineView {
  range: TlRange
  title: string
  days: TlDay[]
  groups: TlGroup[]
  /** Videos without an end of work (the strip above the timeline) */
  none: Video[]
  /** Some video has an end of work (in any period) */
  anyDated: boolean
  /** Number of rows shown */
  shown: number
  /** "3 videos in this period · 2 in other periods · 1 without an end of work · 1 overdue" (the last one in bold) */
  sub: TlSubPart[]
  /** Position of the current-time line (%), -1 when now is not in the period */
  nowPct: number
  /** Minimum width of the timeline part, in pixels (the title column is added) */
  minW: number
}

function tlRow(d: ProfileData, v: Video, sp: TlSpan, R: TlRange, now: Date): TlRow {
  const { s, e, ghost } = sp, format = seriesOf(d, v.seriesId), color = format ? safeColor(format.color) : ''
  const { left, width } = tlBarBox(R, s, e), late = isLate(v, now), ready = v.status === STATUS_READY, done = tlParse(v.doneAt)
  const delay = ready && done ? +done - +e : null, title = videoTitle(v)
  const tip = [title, ghost ? t('End of work {date} · start not chosen', { date: tlEndText(v) }) : `${tlNice(s)} → ${tlEndText(v)} (${tlDur(+e - +s)})`,
    late ? t('Overdue by {time}', { time: tlDur(+now - +e) }) : '',
    delay !== null && done ? (delay > 0 ? t('Delivered {date}, {time} after the end of work', { date: tlNice(done), time: tlDur(delay) }) : t('Delivered {date}, on time', { date: tlNice(done) })) : ''].filter(Boolean).join('\n')
  const sub: TlRowSub = late ? { text: t('Overdue by {time}', { time: tlDur(+now - +e) }), kind: 'late' }
    : delay !== null ? (delay > 0 ? { text: t('Delivered late'), kind: 'deliveredLate' } : { text: t('Delivered on time'), kind: 'onTime' })
      : { text: ghost ? t('Start not chosen') : tlDur(+e - +s), kind: '' }
  return { v, title, format, color, s, e, ghost, left, width, late, ready, cut: s < R.t0,
    donePct: done && done >= R.t0 && done < R.t1 ? tlPct(R, done) : -1, tip, sub }
}

/** The whole Timeline of a period. videos: the visible videos of the plan (filters on), published ones already left
 *  out; colName: the Board column name of a status */
export function timelineView(d: ProfileData, videos: Video[], view: TlView, from: Date, now: Date, colName: (status: string) => string): TimelineView {
  const R = tlRange(view, from), month = view === 'month', today = isoDay(now)
  const dated = videos.map(v => ({ v, sp: tlSpan(v) })).filter((x): x is { v: Video; sp: TlSpan } => !!x.sp)
  const inR = dated.filter(x => x.sp.s < R.t1 && x.sp.e > R.t0)
  const none = videos.filter(v => !tlEnd(v)), late = videos.filter(v => isLate(v, now)).length, other = dated.length - inR.length
  const sub: TlSubPart[] = [
    { text: tn(inR.length, '{n} video in this period', '{n} videos in this period'), bold: false },
    { text: other ? t('{n} in other periods', { n: other }) : '', bold: false },
    { text: none.length ? t('{n} without an end of work', { n: none.length }) : '', bold: false },
    { text: late ? t('{n} overdue', { n: late }) : '', bold: true }
  ].filter(p => p.text)
  const days: TlDay[] = Array.from({ length: R.days }, (_, i) => {
    const x = new Date(R.t0.getFullYear(), R.t0.getMonth(), R.t0.getDate() + i)
    return { iso: isoDay(x), today: isoDay(x) === today, weekend: x.getDay() === 0 || x.getDay() === 6, num: x.getDate(),
      label: month ? fmtDate(x, { weekday: 'narrow' }) : fmtDate(x, { weekday: 'short' }).replace('.', '') }
  })
  const stages = STATUS_KEYS as readonly string[]
  const byStart = (a: { sp: TlSpan }, b: { sp: TlSpan }) => +a.sp.s - +b.sp.s
  const groups = STATUS_KEYS.map(st => ({ status: st as string, name: colName(st), items: inR.filter(x => (x.v.status || 'Idea') === st) }))
  const odd = inR.filter(x => !stages.includes(x.v.status || 'Idea'))
  if (odd.length) groups.push({ status: '', name: t('Other statuses'), items: odd })
  return {
    range: R, title: tlTitle(view, R), days, none, anyDated: dated.length > 0, shown: inR.length, sub,
    groups: groups.filter(g => g.items.length).map(g => ({ status: g.status, name: g.name, rows: g.items.sort(byStart).map(x => tlRow(d, x.v, x.sp, R, now)) })),
    nowPct: now >= R.t0 && now < R.t1 ? tlPct(R, now) : -1,
    minW: month ? R.days * 38 : 7 * 110
  }
}

// ===== Dragging a bar (computer only) =====
/** Moves a moment by n steps. Month: whole days (the time is kept); week: half hours (an end "without a time", 23:59,
 *  first snaps to the half hour) */
export function tlShift(view: TlView, ms: number, n: number): number {
  const x = new Date(ms)
  if (view === 'month') x.setDate(x.getDate() + n)
  else x.setMinutes(Math.round(x.getMinutes() / 30) * 30 + n * 30, 0, 0)
  return +x
}
export interface TlDrag {
  mode: TlMode
  /** Start and end when the drag began (ms) */
  s0: number
  e0: number
  /** Start and end now */
  ns: number
  ne: number
}
/** Start and end after dragging dx pixels on a track w pixels wide: the bar (move), the left edge (start) or the right
 *  edge (end). At least half an hour of work: otherwise the previous position stays */
export function tlDragTo(view: TlView, R: TlRange, g: TlDrag, dx: number, w: number): { ns: number; ne: number } {
  const n = Math.round(dx / w * (view === 'month' ? R.days : R.span / HALF_HOUR))
  let ns = g.s0, ne = g.e0
  if (g.mode === 'move') { ns = tlShift(view, g.s0, n); ne = tlShift(view, g.e0, n) }
  else if (g.mode === 'start') ns = tlShift(view, g.s0, n)
  else ne = tlShift(view, g.e0, n)
  return ne - ns >= HALF_HOUR ? { ns, ne } : { ns: g.ns, ne: g.ne }
}
/** Tooltip while dragging: "start → end", or only the end for a dashed bar (unless its left edge is dragged).
 *  In the Month view an end without a time stays without one */
export function tlDragTip(view: TlView, g: TlDrag & { ghost: boolean }, hasTime: boolean): string {
  const end = new Date(g.ne)
  const endTxt = view === 'month' && !hasTime ? fmtDate(end, { weekday: 'short', day: 'numeric', month: 'short' }) : tlNice(end)
  return g.ghost && g.mode !== 'start' ? t('End of work: {date}', { date: endTxt }) : `${tlNice(new Date(g.ns))} → ${endTxt}`
}
/** Writes a finished drag into the video. A dashed bar (no start) gets a start only from its left edge; the end
 *  (= publication) only when it changed. false = nothing to save */
export function tlDragApply(v: Video, g: TlDrag & { ghost: boolean }, view: TlView): boolean {
  if (g.ns === g.s0 && g.ne === g.e0) return false
  if (!g.ghost || g.mode === 'start') v.workStart = tlFmt(new Date(g.ns))
  if (g.ne !== g.e0) tlSetEnd(v, new Date(g.ne), view === 'month')
  return true
}

// ===== Dropping on a row =====
/** Dates of a video dropped at a point of the timeline (f = 0–1 across the track). Week: from the nearest half hour, for
 *  2 hours; month: that day from 9:00, end of work that day without a time */
export function tlDropTimes(view: TlView, R: TlRange, f: number): { workStart: string; publishDate: string; publishTime: string } {
  f = Math.max(0, Math.min(0.9999, f))
  if (view === 'month') {
    const s = new Date(R.t0.getFullYear(), R.t0.getMonth(), R.t0.getDate() + Math.floor(f * R.days), 9)
    return { workStart: tlFmt(s), publishDate: isoDay(s), publishTime: '' }
  }
  const s = new Date(tlShift(view, +R.t0, Math.round(f * R.span / HALF_HOUR))), e = new Date(+s + 2 * 3600000)
  return { workStart: tlFmt(s), publishDate: isoDay(e), publishTime: tlHhmm(e) }
}
